import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { personalMarketplaceApi } from '../services/api';
import { showToast, askConfirm } from 'shared-ui';

interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  images: string[];
  category: string;
  condition: string;
  location: string;
  status: string;
  views: number;
  saves: number;
  createdAt: string;
  updatedAt?: string;
}

const MyListings: React.FC = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadListings = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await personalMarketplaceApi.listMine();
      if (res?.success && Array.isArray(res?.data)) {
        setListings(res.data);
      } else {
        setError(res?.error || 'Failed to load listings');
        setListings([]);
      }
    } catch (err) {
      console.error('Load listings failed:', err);
      setError('Failed to load listings.');
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadListings();
  }, []);

  const handleDelete = async (id: string) => {
    if (!(await askConfirm({ title: 'Delete this listing?', confirmLabel: 'Delete', danger: true }))) return;
    try {
      const res = await personalMarketplaceApi.delete(id);
      if (res?.success) {
        setListings((prev) => prev.filter((l) => l.id !== id));
      } else {
        showToast(res?.error || 'Delete failed', 'error');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      showToast('Delete failed', 'error');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', flexWrap: 'wrap', gap: '15px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>📋 My Listings</h1>
          <p style={{ margin: '10px 0 0 0', color: '#666' }}>Manage your marketplace listings</p>
        </div>
        <button
          onClick={() => navigate('/marketplace/create')}
          style={{
            padding: '12px 20px',
            background: '#ff6b35',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px',
          }}
        >
          + List an Item
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee', color: '#c00', padding: '12px 16px', borderRadius: '10px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <span style={{ fontSize: '48px' }}>🔍</span>
          <p style={{ color: '#666', marginTop: '15px' }}>Loading your listings...</p>
        </div>
      ) : listings.length === 0 ? (
        <div style={{ background: 'white', padding: '60px', borderRadius: '12px', textAlign: 'center' }}>
          <span style={{ fontSize: '64px' }}>📋</span>
          <h2 style={{ margin: '20px 0 10px 0', color: '#333' }}>You have no listings</h2>
          <p style={{ color: '#666', margin: '0 0 20px 0' }}>Create your first listing to start selling</p>
          <button
            onClick={() => navigate('/marketplace/create')}
            style={{
              padding: '12px 24px',
              background: '#ff6b35',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '14px',
            }}
          >
            + List an Item
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
          {listings.map((listing) => (
            <div
              key={listing.id}
              style={{
                background: 'white',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <div
                style={{
                  height: '180px',
                  background: listing.images?.[0]
                    ? `url(${listing.images[0]}) center/cover`
                    : '#f0f0f0',
                }}
              />
              <div style={{ padding: '16px' }}>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>
                  {listing.category} • {listing.condition}
                </div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#333' }}>{listing.title}</h3>
                <div style={{ fontSize: '18px', fontWeight: '700', color: '#ff6b35', marginBottom: '12px' }}>
                  ${listing.price.toFixed(2)}
                </div>
                <div style={{ fontSize: '11px', color: '#888', marginBottom: '12px' }}>
                  👁 {listing.views} • ❤️ {listing.saves} • {listing.status}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleDelete(listing.id)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      background: '#f0f0f0',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      color: '#c00',
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyListings;
